/**
 * Utilitaires pour le blog
 */

/**
 * Génère un slug URL-friendly à partir d'un titre ou identifiant
 * @param text Le texte à convertir en slug
 * @returns Un slug sans caractères spéciaux
 */
export function generateSlug(text: string): string {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/['']/g, '') // Supprimer les apostrophes
    .replace(/[?]/g, '') // Supprimer les points d'interrogation
    .replace(/[.]/g, '') // Supprimer les points
    .replace(/\s+/g, '-') // Remplacer les espaces par des tirets
    .replace(/[^\w-]/g, '') // Supprimer tous les caractères non-mot
    .replace(/--+/g, '-'); // Remplacer les multiples tirets par un seul
}

/**
 * Extrait un court résumé du contenu de l'article
 * @param content Le contenu de l'article
 * @param summary Résumé optionnel déjà existant
 * @returns Un extrait de texte court
 */
export function getExcerpt(content?: string, summary?: string): string {
  // Utiliser le résumé s'il existe
  if (summary) return summary;
  
  if (content) {
    // Supprimer le frontmatter YAML s'il existe
    const cleanContent = content.replace(/^---[\s\S]*?---/m, '');
    // Supprimer les tags Markdown
    const plainText = cleanContent
      .replace(/#+\s+(.*)/g, '$1') // Titres
      .replace(/(\*\*|__)(.*?)\1/g, '$2') // Gras
      .replace(/(\*|_)(.*?)\1/g, '$2') // Italique
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Liens
      .replace(/`([^`]+)`/g, '$1') // Code inline
      .replace(/```[\s\S]*?```/g, '') // Blocs de code
      .replace(/^\s*[\-\*]\s+(.*)/gm, '$1'); // Listes
    
    // Prendre les premiers ~200 caractères
    return plainText.trim().substring(0, 200) + (plainText.length > 200 ? '...' : '');
  }
  
  return '';
}

/**
 * Formate une date en format localisé
 * @param dateString La date à formater
 * @param locale La locale à utiliser (par défaut fr-FR)
 * @returns Date formatée
 */
export function formatDate(dateString: string, locale: string = 'fr-FR'): string {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (error) {
    return dateString; // En cas d'erreur, renvoyer la chaîne originale
  }
}

/**
 * Interface étendue pour les articles de blog
 */
export interface EnhancedBlogPost {
  id: string;
  title: string;
  date: string;
  category: string;
  summary: string;
  tags: string[] | never[];
  content: string;
  mermaid: string | null;
  language: string;
  image: string;
  slug?: string;
  lang?: string;
  excerpt?: string;
  mermaidDiagrams?: string[];
} 